import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from 'react';
import { describe, PropTypes } from '@amzn/stencil-react-desc';
import { StencilConsumer } from "../context";
import { Col, Row } from "../layout";
import { cssMargin, cssPadding } from "../prop-types";
import { Text } from "../text";
import { omit } from "../utils";
import AvatarImage from "./AvatarImage";
import { defaultRenderLink } from "./AvatarLink";
import AvatarName from "./AvatarName";
import AvatarUsername from "./AvatarUsername";
import { AvatarDefaultProps as defaultProps, BADGE_PHOTO_URL, DEFAULT_COLOR_SCHEME, INVERTED_COLOR_SCHEME, layoutConstants, sharedPropTypes } from "./avatarConstants";
import { AvatarColorScheme, AvatarSize } from "./interfaces";
import { jsx as ___EmotionJSX } from "@emotion/core";

var Avatar = function Avatar(_ref) {
  var username = _ref.username,
      fullName = _ref.fullName,
      _ref$showFullName = _ref.showFullName,
      showFullName = _ref$showFullName === void 0 ? defaultProps.showFullName : _ref$showFullName,
      imageAltText = _ref.imageAltText,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? defaultProps.size : _ref$size,
      _ref$showImage = _ref.showImage,
      showImage = _ref$showImage === void 0 ? defaultProps.showImage : _ref$showImage,
      _ref$showUsername = _ref.showUsername,
      showUsername = _ref$showUsername === void 0 ? defaultProps.showUsername : _ref$showUsername,
      _ref$isUsernameClicka = _ref.isUsernameClickable,
      isUsernameClickable = _ref$isUsernameClicka === void 0 ? defaultProps.isUsernameClickable : _ref$isUsernameClicka,
      _ref$newTabHiddenText = _ref.newTabHiddenText,
      newTabHiddenText = _ref$newTabHiddenText === void 0 ? defaultProps.newTabHiddenText : _ref$newTabHiddenText,
      _ref$colorScheme = _ref.colorScheme,
      colorScheme = _ref$colorScheme === void 0 ? defaultProps.colorScheme : _ref$colorScheme,
      pronouns = _ref.pronouns,
      profileImageUrl = _ref.profileImageUrl,
      padding = _ref.padding,
      margin = _ref.margin,
      _ref$children = _ref.children,
      children = _ref$children === void 0 ? [] : _ref$children,
      _ref$renderLink = _ref.renderLink,
      renderLink = _ref$renderLink === void 0 ? defaultRenderLink : _ref$renderLink,
      employeeId = _ref.employeeId,
      personId = _ref.personId,
      alternateName = _ref.alternateName;
  var layout = layoutConstants[size];
  var textColor = colorScheme === AvatarColorScheme.Inverted ? INVERTED_COLOR_SCHEME.textColor : DEFAULT_COLOR_SCHEME.textColor;
  var hasFullName = showFullName && !!fullName;
  var hasNameOrUsername = hasFullName || showUsername;
  var shouldShowPronouns = !!pronouns && hasFullName && [AvatarSize.Medium, AvatarSize.Large].includes(size);
  var shouldShowAlternateName = !!alternateName && hasFullName && [AvatarSize.Medium, AvatarSize.Large].includes(size);
  var shouldAnnounceImageAltText = !hasNameOrUsername;
  var maxChildren = shouldShowPronouns ? layout.maxChildren - 1 : layout.maxChildren;
  var renderedChildren = React.Children.toArray(children).slice(0, maxChildren);
  var hasChildren = !!renderedChildren.length;
  var isAvatarOnly = !hasNameOrUsername && !hasChildren;
  var imageAriaLabel;

  if (imageAltText) {
    imageAriaLabel = imageAltText;
  } else if (!!fullName) {
    imageAriaLabel = "Employee: ".concat(fullName);
  } else {
    imageAriaLabel = "Alias: ".concat(username);
  }

  var imageSource = personId ? "".concat(BADGE_PHOTO_URL, "?personId=").concat(personId) : employeeId ? "".concat(BADGE_PHOTO_URL, "?employeeId=").concat(employeeId) : "".concat(BADGE_PHOTO_URL, "?uid=").concat(username);

  var usernameComponent = showUsername && ___EmotionJSX(AvatarUsername, {
    username: username,
    fontSize: layout.nameFontSize,
    fontWeight: layout.nameFontWeight,
    lineHeight: layout.nameLineHeight,
    isUsernameClickable: isUsernameClickable,
    newTabHiddenText: newTabHiddenText,
    colorScheme: colorScheme,
    renderLink: renderLink
  });

  return ___EmotionJSX(StencilConsumer, null, function (_ref2) {
    var selectors = _ref2.selectors;
    return ___EmotionJSX(Row, {
      "data-test-component": "Avatar",
      "data-test-id": "Avatar_".concat(username),
      padding: padding,
      margin: margin
    }, showImage && ___EmotionJSX(AvatarImage, {
      selectors: selectors,
      source: profileImageUrl ? profileImageUrl : imageSource,
      size: layout.imageSize,
      marginRight: isAvatarOnly ? 0 : layout.imageSpacing,
      shouldAnnounceImageAltText: shouldAnnounceImageAltText,
      imageAltText: imageAriaLabel
    }), !isAvatarOnly && ___EmotionJSX(Col, {
      gridGap: layout.spacing,
      justifyContent: "center"
    }, hasNameOrUsername && ___EmotionJSX(AvatarName, {
      fullName: fullName,
      showFullName: showFullName,
      usernameComponent: !shouldShowPronouns && usernameComponent,
      pronouns: shouldShowPronouns ? pronouns : undefined,
      colorScheme: colorScheme,
      fontSize: layout.nameFontSize,
      fontWeight: layout.nameFontWeight,
      lineHeight: layout.nameLineHeight,
      alternateName: shouldShowAlternateName ? alternateName : undefined
    }), shouldShowPronouns && usernameComponent, renderedChildren.map(function (child, index) {
      return /*#__PURE__*/React.isValidElement(child) && ___EmotionJSX(Text, {
        lineHeight: layout.lineHeight,
        "data-test-component": "AvatarTextLine",
        fontSize: layout.textFontSize,
        color: textColor,
        key: index
      }, /*#__PURE__*/React.cloneElement(child, {
        lineHeight: layout.lineHeight,
        fontSize: layout.textFontSize
      }));
    })));
  });
};

Avatar.displayName = 'Avatar';
Avatar.defaultProps = _objectSpread({}, defaultProps);
var AvatarWithDocs = describe(Avatar).description('Used to graphically represent Amazonians.');
AvatarWithDocs.propTypes = _objectSpread(_objectSpread(_objectSpread({}, omit(AvatarName.describedPropTypes, AvatarName.privateProps)), sharedPropTypes), {}, {
  username: PropTypes.string.description('Username of the displayed employee.').isRequired,
  employeeId: PropTypes.string.description('Unique employee ID of the displayed employee.'),
  showImage: PropTypes.bool.description('Indicates whether the image should be displayed.'),
  size: PropTypes.oneOf(Object.values(AvatarSize)).description('Size of the avatar.'),
  isUsernameClickable: PropTypes.bool.description("Indicates whether the employee's username should be clickable."),
  renderLink: PropTypes.func.description('Render function for the avatar link').format("({\n  username: string;\n  accessibleUsername: node;\n  selectors: ThemeSelectors;\n  lineHeight: string;\n  fontSize: string;\n  colorScheme: AvatarColorScheme;\n}) => node"),
  padding: cssPadding().description("Sets the CSS 'padding' property for the Avatar."),
  margin: cssMargin().description("Sets the CSS 'margin' property for the Avatar."),
  children: PropTypes.node.description('Additional content to display in the Avatar.'),
  imageAltText: PropTypes.string.description("Optional 'aria-label' for the avatar image. If not provided attempts to create from fullName, otherwise falls back to username. Is only added if no fullName or username are to be displayed with the avatar image."),
  profileImageUrl: PropTypes.string.description('Overwrites the default avatar image url.')
});
export default AvatarWithDocs;